import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

/**
 * LT-18c §2 — material unit lookup, key → label.
 *
 * One fetch of `/material-units?include_archived=true`. `labelFor(key)`
 * resolves against ALL rows so a material whose unit Chris later archived
 * still renders "m²", not "m2". Falls back to the raw value on an
 * unrecognised key — an orphan should surface visibly, never blank.
 *
 * Same shape as `useJobTypeLibrary`. Never render `material.unit` or
 * `material.coverage_unit` verbatim; go through this.
 */
export function useMaterialUnits() {
  const [units, setUnits] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/material-units", { params: { include_archived: true } })
      .then((r) => { if (alive) setUnits(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setUnits([]); });
    return () => { alive = false; };
  }, []);

  const keyToLabel = useMemo(() => {
    const m = {};
    for (const u of units) if (u.key) m[u.key] = u.label || u.key;
    return m;
  }, [units]);

  const labelFor = useCallback(
    (key) => (key ? (keyToLabel[key] || key) : ""),
    [keyToLabel],
  );

  return { units, keyToLabel, labelFor };
}
